if (!customElements.get('liene-blog-topics')) {
  customElements.define('liene-blog-topics', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.config = this.querySelector('[data-topic-config]').dataset;
      this.labels = new Map(
  [...this.querySelectorAll('[data-topic-config] [data-tag]')]
    .filter(el => el.dataset.tag.trim() !== '')
    .map(el => [el.dataset.tag, el])
);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-retry]')) { this.load(); return; }
        const link = event.target.closest('a[data-topic], a[data-page-link]');
        if (!this.posts || !link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        history.pushState({}, '', link.href);
        this.render(link.hasAttribute('data-topic') ? 'topic' : 'page');
      }, { signal: this.events.signal });
      window.addEventListener('popstate', () => { if (this.posts) this.render(); }, { signal: this.events.signal });
      this.load();
    }
    disconnectedCallback() { this.events?.abort(); this.request?.abort(); }
    async load() {
      this.request?.abort();
      const request = this.request = new AbortController();
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-load-notice]').hidden = true;
      try {
        // Tokenless Storefront API includes published articles from every blog.
        // Follow every cursor: neither article cards nor tags are limited to the first batch.
        const posts = new Map();
        const cursors = new Set();
        let after = null;
        do {
          const response = await fetch('/api/2026-01/graphql.json', {
            method: 'POST', signal: request.signal,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: `query BlogTopics($after: String) {
              articles(first: 50, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
                nodes { id title handle tags publishedAt onlineStoreUrl excerpt(truncateAt: 300) content(truncateAt: 300)
                  authorV2 { name } blog { handle } image { url altText width height } }
                pageInfo { hasNextPage endCursor }
              }
            }`, variables: { after } })
          });
          if (!response.ok) throw new Error('Article request failed');
          const result = await response.json();
          const connection = result.data?.articles;
          if (result.errors?.length || !connection?.nodes || !connection.pageInfo) throw new Error('Missing article results');
          for (const post of connection.nodes) {
            if (post.onlineStoreUrl && Date.parse(post.publishedAt) <= Date.now()) posts.set(post.id, post);
          }
          after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
          if (connection.pageInfo.hasNextPage && (!after || cursors.has(after))) throw new Error('Invalid article cursor');
          cursors.add(after);
        } while (after);
        if (request.signal.aborted) return;
        this.posts = [...posts.values()].sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt) || a.id.localeCompare(b.id));
        this.tags = this.labels.size > 0
  ? [...this.labels.keys()]
  : [...new Set(this.posts.flatMap(post => post.tags))]
      .sort((a, b) => a.localeCompare(b));
        this.render();
      } catch (error) {
        if (!request.signal.aborted) this.querySelector('[data-load-notice]').hidden = false;
      } finally {
        if (this.request === request) this.removeAttribute('aria-busy');
      }
    }
    state() {
      const url = new URL(location.href);
      // Preserve old incoming tagged links while new links stay on the aggregate page.
      let topic = url.searchParams.get('topic');
      if (topic === null && location.pathname.includes('/tagged/')) {
        topic = this.querySelector('[data-topic][aria-current="page"]')?.dataset.topic || '';
      }
      return { topic: topic || '', page: Math.max(1, Number.parseInt(url.searchParams.get('topic_page') || '1', 10) || 1) };
    }
    url(topic, page = 1) {
      const url = new URL(location.href);
      url.pathname = new URL(this.dataset.blogUrl, location.origin).pathname;
      for (const key of ['page', 'section_id', 'topic', 'topic_page']) url.searchParams.delete(key);
      if (topic) url.searchParams.set('topic', topic);
      if (page > 1) url.searchParams.set('topic_page', page);
      return url;
    }
    element(tag, className, text) {
      const el = document.createElement(tag);
      if (className) el.className = className;
      if (text !== undefined) el.textContent = text;
      return el;
    }
    render(focusTarget) {
      const { topic, page: requestedPage } = this.state();
      const posts = topic ? this.posts.filter(post => post.tags.includes(topic)) : this.posts;
      const pages = Math.max(1, Math.ceil(posts.length / 9));
      const page = Math.min(requestedPage, pages);
      const tabs = this.querySelector('.lbt__tabs');
      tabs.replaceChildren();
      for (const tag of ['', ...this.tags]) {
        const config = this.labels.get(tag);
        const link = this.element('a', 'lbt__tab', tag ? config?.dataset.label || tag : this.config.allLabel);
        link.href = this.url(tag);
        link.dataset.topic = tag;
        if (config) for (const attr of config.attributes) {
          if (attr.name.startsWith('data-shopify-')) link.setAttribute(attr.name, attr.value);
        }
        if (tag === topic) { link.classList.add('is-active'); link.setAttribute('aria-current', 'page'); }
        tabs.append(link);
      }
      const grid = this.querySelector('.lbt__grid');
      grid.replaceChildren(...posts.slice((page - 1) * 9, page * 9).map(post => this.card(post)));
      if (!posts.length) grid.append(this.element('p', 'lbt__empty', this.config.emptyText));
      this.querySelector('.lbt__pagination')?.remove();
      if (pages > 1) {
        const pagination = this.element('nav', 'lbt__pagination');
        pagination.setAttribute('aria-label', 'Blog pagination');
        const add = (number, text, label) => {
          const enabled = number >= 1 && number <= pages && number !== page;
          const el = this.element(enabled ? 'a' : 'span', number === page ? 'is-active' : enabled ? '' : 'is-disabled', text);
          el.setAttribute('aria-label', label);
          if (enabled) { el.href = this.url(topic, number); el.dataset.pageLink = ''; }
          if (number === page) el.setAttribute('aria-current', 'page');
          pagination.append(el);
        };
        add(page - 1, '‹', 'Previous page');
        let last = 0;
        for (let n = 1; n <= pages; n++) {
          if (n !== 1 && n !== pages && Math.abs(n - page) > 2) continue;
          if (last && n - last > 1) pagination.append(this.element('span', '', '…'));
          add(n, String(n), `Page ${n}`);
          last = n;
        }
        add(page + 1, '›', 'Next page');
        this.querySelector('.lbt__results').append(pagination);
      }
      this.querySelector('.lbt__status').textContent = `${page} / ${pages}`;
      if (focusTarget) {
        const active = this.querySelector('[data-topic][aria-current]');
        const target = focusTarget === 'topic' ? active : this.querySelector('.lbt__pagination [aria-current]');
        if (target) { target.tabIndex = 0; target.focus({ preventScroll: true }); }
        if (active) this.querySelector('.lbt__nav').scrollLeft = active.offsetLeft - this.querySelector('.lbt__nav').offsetLeft - 16;
        this.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
    }
    card(post) {
      const card = this.element('article', 'lbt-card');
      const link = this.element('a', 'lbt-card__link');
      const root = this.config.rootUrl || '/';
      link.href = `${root.endsWith('/') ? root : root + '/'}blogs/${encodeURIComponent(post.blog.handle)}/${encodeURIComponent(post.handle)}`;
      const media = this.element('div', 'lbt-card__media');
      if (post.image) {
        const img = this.element('img');
        const imageUrl = width => { const url = new URL(post.image.url); url.searchParams.set('width', width); return url.href; };
        img.src = imageUrl(1200);
        img.srcset = [360, 540, 720, 960, 1200].map(width => `${imageUrl(width)} ${width}w`).join(', ');
        img.sizes = '(max-width: 699px) calc(100vw - 32px), (max-width: 989px) 46vw, 410px';
        img.alt = '';
        img.loading = 'lazy';
        img.width = post.image.width;
        img.height = post.image.height;
        media.append(img);
      }
      const title = this.element('h3', 'lbt-card__title', post.title);
      title.id = `${this.id}-Article-${post.id.split('/').pop()}`;
      link.setAttribute('aria-labelledby', title.id);
      link.append(media, title, this.element('p', 'lbt-card__excerpt', post.excerpt || post.content));
      const meta = this.element('div', 'lbt-card__meta');
      meta.append(this.element('span', 'lbt-card__author', this.config.showAuthor === 'true' && post.authorV2?.name ? `${this.config.authorPrefix} ${post.authorV2.name}` : ''));
      if (this.config.showDate === 'true') {
        const date = this.element('time', '', new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(new Date(post.publishedAt)));
        date.dateTime = post.publishedAt.slice(0, 10);
        meta.append(date);
      }
      card.append(link, meta);
      return card;
    }
  });
}
